import { useCallback, useEffect, useRef, useState } from 'react';
import { api, downloadFile, getToken, triggerDownload, type LibraryFile } from '../shared/api.ts';
import { formatBytes, formatWhen } from '../shared/format.ts';
import { Thumb } from './Thumb.tsx';

const KINDS = [
  { value: '', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'other', label: 'Other' },
] as const;

export function Library({
  selection,
  onSelectionChange,
  refreshKey,
}: {
  selection: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  refreshKey: number;
}): React.JSX.Element {
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [totalBytes, setTotalBytes] = useState(0);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<number>(0);

  const load = useCallback(
    async (nextCursor: string | null, append: boolean, q: string, k: string) => {
      setLoading(true);
      try {
        const page = await api.files({ cursor: nextCursor, q, kind: k });
        setFiles((prev) => (append ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setTotalBytes(page.totalBytes);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => void load(null, false, query, kind), query ? 220 : 0);
    return () => window.clearTimeout(debounce.current);
  }, [query, kind, refreshKey, load]);

  const toggle = (id: string): void => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const selected = files.filter((f) => selection.has(f.id));

  const downloadSelection = async (): Promise<void> => {
    if (selected.length === 0) return;
    if (selected.length === 1 && selected[0]) {
      await downloadFile(selected[0].id, selected[0].name);
      return;
    }
    const { url } = await api.archive(selected.map((f) => f.id));
    // the archive token is the credential, so a plain navigation is enough
    triggerDownload(url, 'drop.zip');
  };

  const deleteSelection = async (): Promise<void> => {
    if (selected.length === 0) return;
    await api.deleteFiles(selected.map((f) => f.id));
    onSelectionChange(new Set());
    void load(null, false, query, kind);
  };

  return (
    <div className="pane">
      <div className="library__controls">
        <input
          className="input"
          type="search"
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="library__kinds">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              className={`chip${kind === k.value ? ' is-on' : ''}`}
              onClick={() => setKind(k.value)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="pane__error mono">{error}</p> : null}

      {files.length === 0 && !loading ? (
        <p className="pane__empty">{query || kind ? 'Nothing matches.' : 'Library is empty.'}</p>
      ) : (
        <ul className="rows">
          {files.map((file) => (
            <li key={file.id}>
              <label className={`row${selection.has(file.id) ? ' is-selected' : ''}`}>
                <input
                  type="checkbox"
                  className="row__check"
                  checked={selection.has(file.id)}
                  onChange={() => toggle(file.id)}
                />
                <Thumb id={file.id} mime={file.mime} hasThumbnail={file.hasThumbnail} name={file.name} />
                <span className="row__body">
                  <span className="row__name">
                    {file.relativePath ? <span className="mono row__path">{file.relativePath}/</span> : null}
                    {file.name}
                  </span>
                  <span className="row__facts mono">
                    {formatBytes(file.size)} · {formatWhen(file.keptAt ?? file.createdAt)}
                  </span>
                </span>
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={(e) => {
                    e.preventDefault();
                    void downloadFile(file.id, file.name);
                  }}
                >
                  Get
                </button>
              </label>
            </li>
          ))}
        </ul>
      )}

      {cursor ? (
        <button
          type="button"
          className="button library__more"
          onClick={() => void load(cursor, true, query, kind)}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      ) : null}

      <p className="library__total mono">
        {files.length} shown · {formatBytes(totalBytes)} stored
      </p>

      {selected.length > 0 ? (
        <div className="actionbar">
          <span className="mono">{selected.length} selected</span>
          <div className="actionbar__buttons">
            <button type="button" className="button button--quiet" onClick={() => onSelectionChange(new Set())}>
              Clear
            </button>
            <button type="button" className="button button--quiet button--danger" onClick={() => void deleteSelection()}>
              Delete
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={() => void downloadSelection()}
              disabled={!getToken()}
            >
              Download
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
